import { SignatureV4 } from '@smithy/signature-v4';
import { Sha256 } from '@aws-crypto/sha256-browser';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import type { AwsCredentialIdentity, Provider } from '@aws-sdk/types';

// Configuration from environment
const AWS_REGION = import.meta.env.VITE_AWS_REGION || 'us-east-1';
const IDENTITY_POOL_ID = import.meta.env.VITE_COGNITO_IDENTITY_POOL_ID;
const USER_POOL_ID = import.meta.env.VITE_COGNITO_USER_POOL_ID;
const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT || '';

// Authenticated user's ID token (set after Cognito User Pool sign-in)
let currentIdToken: string | null = null;
let credentialProvider: Provider<AwsCredentialIdentity> | null = null;
let signer: SignatureV4 | null = null;

/**
 * Set the Cognito User Pool ID token for authenticated Identity Pool access.
 * Pass null to clear (on sign-out).
 */
export function setAuthToken(idToken: string | null): void {
  currentIdToken = idToken;
  // Reset provider and signer so they're recreated with the new token
  credentialProvider = null;
  signer = null;
}

function getCredentialProvider(): Provider<AwsCredentialIdentity> {
  if (!credentialProvider) {
    if (IDENTITY_POOL_ID) {
      const logins =
        currentIdToken && USER_POOL_ID
          ? { [`cognito-idp.${AWS_REGION}.amazonaws.com/${USER_POOL_ID}`]: currentIdToken }
          : undefined;

      console.debug('[awsAuth] Creating credential provider', {
        region: AWS_REGION,
        identityPoolId: IDENTITY_POOL_ID,
        userPoolId: USER_POOL_ID,
        hasIdToken: !!currentIdToken,
        loginsKey: logins ? Object.keys(logins)[0] : 'none',
      });

      credentialProvider = fromCognitoIdentityPool({
        identityPoolId: IDENTITY_POOL_ID,
        clientConfig: { region: AWS_REGION },
        logins,
      });
    } else {
      // For local development, credentials should come from environment
      credentialProvider = async () => ({
        accessKeyId: import.meta.env.VITE_AWS_ACCESS_KEY_ID || '',
        secretAccessKey: import.meta.env.VITE_AWS_SECRET_ACCESS_KEY || '',
        sessionToken: import.meta.env.VITE_AWS_SESSION_TOKEN,
      });
    }
  }
  return credentialProvider;
}

function getSigner(): SignatureV4 {
  if (!signer) {
    signer = new SignatureV4({
      credentials: getCredentialProvider(),
      region: AWS_REGION,
      service: 'execute-api',
      sha256: Sha256,
    });
  }
  return signer;
}

export interface SignedRequestInit extends RequestInit {
  headers: Headers;
}

/**
 * Sign a fetch request with AWS SigV4
 */
export async function signRequest(
  url: string,
  init: RequestInit = {}
): Promise<SignedRequestInit> {
  const parsedUrl = new URL(url, window.location.origin);

  // Parse query string into key-value pairs for SigV4 canonical query
  const query: Record<string, string> = {};
  parsedUrl.searchParams.forEach((value, key) => {
    query[key] = value;
  });

  // Build the request to sign — path and query must be separate fields
  // so SigV4 computes the canonical request correctly
  const request = {
    method: init.method || 'GET',
    protocol: parsedUrl.protocol,
    hostname: parsedUrl.hostname,
    port: parsedUrl.port ? parseInt(parsedUrl.port, 10) : undefined,
    path: parsedUrl.pathname,
    query,
    headers: {
      host: parsedUrl.host,
      'content-type': 'application/json',
      ...(init.headers as Record<string, string>),
    },
    body: init.body as string | undefined,
  };

  // Sign the request
  const signedRequest = await getSigner().sign(request);

  // Convert to fetch-compatible format
  const signedHeaders = new Headers();
  for (const [key, value] of Object.entries(signedRequest.headers)) {
    if (value) {
      signedHeaders.set(key, value);
    }
  }

  return {
    ...init,
    headers: signedHeaders,
  };
}

/**
 * Check if IAM auth is enabled
 */
export function isIamAuthEnabled(): boolean {
  // IAM auth is enabled if we have an identity pool or explicit credentials
  return !!(
    IDENTITY_POOL_ID ||
    import.meta.env.VITE_AWS_ACCESS_KEY_ID ||
    import.meta.env.VITE_ENABLE_IAM_AUTH === 'true'
  );
}

/**
 * Get the configured API endpoint
 */
export function getApiEndpoint(): string {
  return API_ENDPOINT;
}

/**
 * Get the AWS region
 */
export function getAwsRegion(): string {
  return AWS_REGION;
}
